<template>
    <header class="step-header" :class="{ 'is-centred': align === 'center', 'is-display': size === 'display' }">
        <div class="titles">
            <div v-if="$slots.mark" class="title-row">
                <slot name="mark"></slot>
                <h1 class="step-title" :class="titleType">{{ title }}</h1>
            </div>
            <h1 v-else class="step-title" :class="titleType">{{ title }}</h1>
            <p v-if="lede || $slots.default" class="lede" :class="ledeType">
                <slot>{{ lede }}</slot>
            </p>
            <!-- A div, not a p: the calendar step puts a <details> in here, and a block
                 inside a paragraph is auto-closed by the parser, which broke the gap. -->
            <div v-if="$slots.note" class="note">
                <slot name="note"></slot>
            </div>
        </div>
        <hr class="rule" />
    </header>
</template>

<script setup lang="ts">
/**
 * Default to section-heading size because the section rail already identifies the step. Reserve display
 * size for opening claims.
 */
import { computed } from "vue";

const { align = "start", size = "step" } = defineProps<{
    title: string;
    lede?: string;
    align?: "start" | "center";
    size?: "step" | "display";
}>();

/* Bind one typography utility at a time; layering both makes the result depend on CSS emission order. */
const titleType = computed(() => (size === "display" ? "display-medium" : "title-subsection"));
const ledeType = computed(() => (size === "display" ? "body" : "body-small"));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step-header {
        @apply flex flex-col gap-5;
    }

    .titles {
        @apply flex flex-col gap-1;
    }

    .title-row {
        @apply flex items-center gap-3;
    }

    /* Not `.title`: Tailwind generates a `title` utility of the same name, and the
       utility layer wins, which took this to 30px on desktop. */
    .step-title {
        @apply text-ink;
    }

    .lede {
        @apply text-balance text-ink-muted;
    }

    .note {
        @apply details mt-1 flex flex-wrap items-center gap-2 text-ink-muted;
    }

    /*
     * Limit the lede independently of the wider display title. Increase title-to-lede spacing; increase
     * divider spacing to prevent it from looking like the lede's underline.
     */
    .step-header.is-display {
        @apply gap-8;
    }

    .is-display .titles {
        @apply gap-5;
    }

    .is-display .lede {
        @apply mx-auto max-w-[56ch];
    }

    .is-centred .titles {
        @apply items-center text-center;
    }

    .is-centred .title-row,
    .is-centred .note {
        @apply justify-center;
    }

    .rule {
        @apply border-t border-border;
    }
}
</style>
